import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ClipboardList, PackageCheck, Sparkles, Store, Truck, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tradeLabel } from "@/lib/trades";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function JobChecklist({ job, onChange }) {
  const { user } = useAuth();
  const canOffice = ["owner", "dispatcher"].includes(user?.role);
  const canUse = ["owner", "dispatcher", "technician"].includes(user?.role);
  const [busy, setBusy] = useState(false);
  const checklist = job?.materials_checklist || [];
  const { data: invCheck } = useQuery({
    queryKey: ["inventory-check", job?.id, checklist.length],
    enabled: !!job?.id && checklist.length > 0,
    queryFn: async () => (await api.get(`/jobs/${job.id}/inventory-check`)).data,
  });
  const avail = {};
  (invCheck?.lines || []).forEach((l) => { avail[l.id] = l; });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/generate-checklist`, {});
      toast.success(`Job checklist built from ${data.source_quote || "proposal"} (${data.count} items)`);
      onChange?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const aiPredict = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/ai-checklist`, {});
      toast.success(`AI added ${data.added} item(s)${data.priced ? ` · ${data.priced} auto-priced from pricebook` : ""}`);
      onChange?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  const toggle = async (it) => {
    try {
      await api.patch(`/jobs/${job.id}/materials-checklist/${it.id}`, { used: !it.used });
      onChange?.();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!checklist.length) {
    return (
      <div className="card-tac mt-6 p-5" data-testid="job-checklist-empty">
        <div className="mb-2 flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500">
          <ClipboardList className="h-4 w-4 text-cyan-400" /> Equipment & materials checklist
        </div>
        <p className="text-sm text-slate-400">No checklist yet. It is generated automatically when the customer signs the proposal, or build it now from the latest proposal.</p>
        {canOffice && (
          <button data-testid="generate-checklist-btn" onClick={generate} disabled={busy}
            className="btn-amber mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" /> {busy ? "Building…" : "Generate from proposal"}
          </button>
        )}
        {canOffice && (
          <button data-testid="ai-checklist-btn" onClick={aiPredict} disabled={busy}
            className="mt-3 ml-2 inline-flex items-center gap-2 rounded-full border border-cyan-600/50 px-4 py-2 text-xs text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50">
            <Sparkles className="h-3.5 w-3.5" /> {busy ? "Predicting…" : "AI predict materials & tools"}
          </button>
        )}
      </div>
    );
  }

  const usedCount = checklist.filter((c) => c.used).length;
  const totalCost = checklist.reduce((s, c) => s + (c.qty || 0) * (c.unit_cost || 0), 0);

  return (
    <div className="card-tac mt-6 p-5" data-testid="job-checklist">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-mono-tac text-xs uppercase tracking-widest text-slate-500">
          <ClipboardList className="h-4 w-4 text-cyan-400" /> Equipment & materials checklist
        </div>
        <div className="flex items-center gap-3 text-xs">
          {job.checklist_source_number && <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 font-mono-tac text-[10px] text-emerald-300">from {job.checklist_source_number}</span>}
          {invCheck && (invCheck.fully_stocked
            ? <span data-testid="fleet-stock-ok" className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300"><CheckCircle2 className="h-3 w-3" /> Fleet stocked</span>
            : <span data-testid="fleet-stock-short" className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-300"><AlertTriangle className="h-3 w-3" /> {invCheck.shortfall_count} short</span>)}
          <span className="font-mono-tac text-slate-400" data-testid="checklist-progress">{usedCount}/{checklist.length} used</span>
          {canOffice && <button data-testid="ai-checklist-btn" onClick={aiPredict} disabled={busy} className="inline-flex items-center gap-1 rounded-full border border-cyan-600/50 px-2.5 py-0.5 text-[10px] text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-50"><Sparkles className="h-3 w-3" /> {busy ? "…" : "AI predict"}</button>}
        </div>
      </div>
      <div className="space-y-2">
        {checklist.map((c) => {
          const a = avail[c.id];
          return (
          <div key={c.id} data-testid={`checklist-item-${c.id}`} className="flex items-center gap-3 rounded-lg border border-slate-800 bg-[#0d1420] px-3 py-2 text-sm">
            {canUse ? (
              <input type="checkbox" data-testid={`checklist-used-${c.id}`} checked={!!c.used} onChange={() => toggle(c)} className="h-4 w-4 accent-emerald-500" />
            ) : <PackageCheck className="h-4 w-4 text-slate-600" />}
            <span className={`flex-1 truncate ${c.used ? "text-slate-500 line-through" : "text-white"}`}>{c.item}</span>
            {a && (a.shortfall > 0
              ? <span data-testid={`stock-short-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-300"><Truck className="h-3 w-3" /> short {a.shortfall}</span>
              : <span data-testid={`stock-ok-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300"><Truck className="h-3 w-3" /> {a.available} in fleet</span>)}
            {c.trade && <span className="hidden rounded bg-slate-800/70 px-1.5 py-0.5 font-mono-tac text-[9px] text-slate-400 sm:inline">{tradeLabel(c.trade)}</span>}
            {c.vendor && <span className="hidden items-center gap-1 text-[11px] text-slate-500 md:inline-flex"><Store className="h-3 w-3" /> {c.vendor}</span>}
            <span className="font-mono-tac text-xs text-slate-500">×{c.qty}</span>
            {c.unit_cost != null && <span className="w-20 text-right font-mono-tac text-xs text-slate-400">{money(c.unit_cost)}</span>}
          </div>
          );
        })}
      </div>
      <p className="mt-3 font-mono-tac text-[10px] text-slate-600">Estimated materials cost {money(totalCost)} · check items off as they're consumed on the job.</p>
    </div>
  );
}
